import {
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import { Formik } from "formik";
import { isEmpty, omit } from "lodash-es";
import moment, { Moment } from "moment";
import { forwardRef, useMemo, useState } from "react";
import * as Yup from "yup";

import FormContentBlock from "@/App/Layouts/FormContentBlock";
import { AppDateTimePicker } from "@/common/components/Datetime/AppDateTimePicker";
import { DATETIME_FORMATS } from "@/common/constants/common";
import { useBreadcrumbReplacements } from "@/common/contexts/breadcrumbs";
import { FileItem } from "@/common/fileItem";
import { ArrayHelper } from "@/common/helpers/array";
import { EntityHelper } from "@/common/helpers/entity";
import { DamageDetectionHelper } from "@/common/helpers/entity/damageDetection";
import { IdHelper } from "@/common/helpers/id";
import { MemoHelper } from "@/common/helpers/memo";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useTenantProfile } from "@/common/hooks/entity/tenant/useTenantProfile";
import useMounted from "@/common/hooks/mount/useMounted";
import useAppSnackbar from "@/common/hooks/useAppSnackbar";
import { useUserAffiliation } from "@/common/hooks/user/useUserAffiliation";
import { useUserProfile } from "@/common/hooks/user/useUserProfile";
import { BaseFormikValues } from "@/common/ts/error";
import { ValidationHelper } from "@/common/validation";
import { apiClient } from "@/core/api/ApiClient";
import {
  ContractDto,
  ContractStage,
  CreateDamageDetectionDto,
  CreateDamageDetectionItemDto,
  DamageDetectionDto,
  DamageDetectionItemDto,
  DamageTypeDto,
  EntityType,
  GeneralAttachmentDto,
  GeneralAttachmentInputDto,
  UpdateDamageDetectionDto,
  UpdateDamageDetectionItemDto,
  VehicleDto,
  VehiclePartTypeDto,
  VehicleVisualModelDto,
  VehicleVisualModelSvgPathMetadataDto,
  VehicleVisualModelType,
} from "@/core/api/generated/v1";

import AppIconButton from "../../Button/AppIconButton";
import { DevRenderCount } from "../../Dev/DevRenderCount";
import FoldableBlock from "../../Display/FoldableBlock";
import GeneralValidationError from "../../Error/GeneralValidationError";
import FileUploader from "../../Files/FileUploader";
import FormActions from "../../Form/FormActions";
import { FormControlPad } from "../../Form/FormControlPad";
import AppTextArea from "../../Form/Input/AppTextArea";
import FuelLevelInput from "../../Form/Input/FuelLevelInput";
import MileageInput from "../../Form/Input/MileageInput";
import { OutlinedInputWrapper } from "../../Form/OutlinedInputWrapper";
import AppIcon from "../../Icons/AppIcon";
import MemoWrapper from "../../Memo/MemoWrapper";
import ConfirmationModal from "../../Modals/ConfirmationModal";
import AppTypography from "../../Text/AppTypography";
import AppTooltip from "../../Tooltip/AppTooltip";
import ContractAutocompleteOrCreate from "../Contract/ContractAutocompleteOrCreate";
import EntityAffiliationInputV2 from "../EntityAffiliation/EntityAffiliationInputV2";
import InferEntityAffiliationFromVehicleFormikComputedField from "../EntityAffiliation/InferEntityAffiliationFromVehicleFormikComputedField";
import GeneralAttachedTagsInput from "../General/GeneralTag/GeneralAttachedTagsInput";
import VehicleAutocompleteOrCreate from "../Vehicle/VehicleAutocompleteOrCreate";
import VehicleVisualModelAutoSelector from "../VehicleVisualModel/VehicleVisualModelAutoSelector";
import BaseEntityCreateUpdate, {
  BaseEntityCreateUpdateInheritableProps,
  FormWrapperHandle,
} from "../components/BaseEntityCreateUpdate";
import { BaseEntityCreateUpdateExtras } from "../components/BaseEntityCreateUpdateExtras";
import { DamageDetectionItemsInput } from "./CreateUpdate/DamageDetectionItemsInput";
import DamageDetectionSendToExternalDamageCostEvaluationInlineInputModal from "./DamageDetectionSendToExternalDamageCostEvaluationInlineInputModal";

export function mapItemToInputDto(x: DamageDetectionItemDto): DamageDetectionItemValues {
  return {
    ...x,
    id: x.id || IdHelper.newTempFeId(),
    partTypeId: x.partType?.id,
    partTypeLocal: x.partType,
    damageTypeId: x.damageType?.id,
    damageTypeLocal: x.damageType,
    notes: x.notes || undefined,
    initialAttachments: x.attachments && x.attachments.length > 0 ? x.attachments : undefined,
    attachments: x.attachments && x.attachments.length > 0 ? x.attachments : undefined,
    area: x.area,
    projection: x.projection,
    point: x.point,
  };
}

export type DamageDetectionItemValues = Omit<DamageDetectionItemDto, "attachments"> &
  Omit<CreateDamageDetectionItemDto, "attachments"> &
  Omit<UpdateDamageDetectionItemDto, "attachments"> & {
    attachments?: Array<GeneralAttachmentInputDto | GeneralAttachmentDto>;
    initialAttachments?: Array<GeneralAttachmentInputDto | GeneralAttachmentDto>;
    svgPathMetadata?: VehicleVisualModelSvgPathMetadataDto | null;
    partTypeLocal?: VehiclePartTypeDto | null;
    damageTypeLocal?: DamageTypeDto | null;
  };

export type DamageDetectionValues = BaseFormikValues &
  (Omit<CreateDamageDetectionDto, "items"> & Omit<UpdateDamageDetectionDto, "items">) & {
    initialAttachments: DamageDetectionDto["attachments"];
    items: DamageDetectionItemValues[];
    isAnyDamagesOnVehicle: boolean;
    vehicle: VehicleDto | undefined;
    isAttachmentFilesUploading: boolean;
    isAttachmentFilesHaveErrors: boolean;
  };

type DefaultValues = {
  vehicleId?: CreateDamageDetectionDto["vehicleId"];
  contractId?: CreateDamageDetectionDto["contractId"];
  tenantRequestsMeta?: CreateDamageDetectionDto["tenantRequestsMeta"];
  inspectedAt?: CreateDamageDetectionDto["inspectedAt"];
};

export type DamageDetectionCreateUpdateHandle = FormWrapperHandle;

export interface DamageDetectionCreateUpdateProps extends BaseEntityCreateUpdateInheritableProps<
  DamageDetectionDto,
  DefaultValues
> {
  damageDetectionId?: string;
}

export default forwardRef<DamageDetectionCreateUpdateHandle, DamageDetectionCreateUpdateProps>(
  function DamageDetectionCreateUpdate(
    {
      damageDetectionId,
      defaultValues,
      onCreate,
      onUpdate,
      onSave,
      onCancel,
    }: DamageDetectionCreateUpdateProps,
    ref,
  ) {
    const formInfo = useMemo(
      () => EntityHelper.getEntityInputFormInfoByEntityId(damageDetectionId),
      [damageDetectionId],
    );

    const mounted = useMounted();
    const { enqueueSnackbar } = useAppSnackbar();

    const profile = useUserProfile();
    const tenantProfile = useTenantProfile();
    const userAffiliation = useUserAffiliation();

    const [contract] = useState<ContractDto | undefined>(undefined);
    const [visualModel, setVisualModel] = useState<VehicleVisualModelDto | undefined>(undefined);
    const [confirmVehicleHasNoNewDamageOpen, setConfirmVehicleHasNoNewDamageOpen] = useState(false);
    const [
      isSendToExternalDamageCostEvaluationInlineInputModalOpen,
      setIsSendToExternalDamageCostEvaluationInlineInputModalOpen,
    ] = useState(false);

    const damageDetectionRequest = useApiRequest(
      apiClient.damageDetectionsApi.apiV1DamageDetectionsDamageDetectionIdGet,
      {
        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
        damageDetectionId: damageDetectionId || "",
      },
      {
        deps: [damageDetectionId],
        skip: !damageDetectionId,
      },
    );
    const damageDetection = damageDetectionRequest?.data;

    const vehicleIdComputed = defaultValues?.vehicleId || damageDetection?.vehicle?.id;
    const contractIdComputed = defaultValues?.contractId || damageDetection?.contract?.id;

    const formPerms = useMemo(
      () =>
        DamageDetectionHelper.getEntityFormPermissions({
          formInfo,
          entity: damageDetection,
        }),
      [formInfo, damageDetection],
    );

    useBreadcrumbReplacements({
      waitTimeout: 10_000,
      idBreadcrumb: damageDetection && {
        idValue: damageDetection.id!,
        newTitle: damageDetection.localNumber || "",
      },
    });

    return (
      <BaseEntityCreateUpdate
        entityType={EntityType.DamageDetection}
        entityId={damageDetectionId}
        entity={damageDetection}
        entityRequest={damageDetectionRequest}
        isIniting={
          damageDetectionRequest.isLoading ||
          !profile ||
          !tenantProfile ||
          userAffiliation.isIniting
        }
      >
        <Formik<DamageDetectionValues>
          initialValues={{
            contractId: damageDetection?.contract?.id || defaultValues?.contractId || undefined,
            vehicleId: damageDetection?.vehicle?.id || defaultValues?.vehicleId || undefined,
            vehicle: undefined,
            departmentIds:
              (!isEmpty(damageDetection?.departmentIds)
                ? damageDetection?.departmentIds
                : undefined) ||
              (!isEmpty(userAffiliation.departments)
                ? userAffiliation.departments?.map((d) => d.id!)
                : undefined),
            locationIds:
              (!isEmpty(damageDetection?.locationIds) ? damageDetection?.locationIds : undefined) ||
              (!isEmpty(userAffiliation.locations)
                ? userAffiliation.locations?.map((l) => l.id!)
                : undefined),
            externalNumber: damageDetection?.externalNumber || undefined,
            inspectedAt:
              damageDetection?.inspectedAt ||
              defaultValues?.inspectedAt ||
              moment().format(DATETIME_FORMATS.DISPLAY_DATETIME),
            inspector: {
              isCurrentUser: true,
              ...(damageDetection?.inspector || {}),
              phoneNumber: damageDetection?.inspector?.phoneNumber,
            },
            isUseSupplierBranding: damageDetection ? damageDetection.isUseSupplierBranding : true,
            mileage: damageDetection?.mileage || undefined,
            fuelLevel: damageDetection?.fuelLevel || undefined,
            isAnyDamagesOnVehicle: !damageDetection?.isNoDamagesDetected || true,
            isSendToCustomer: damageDetection?.isSendToCustomer ?? undefined,
            notes: damageDetection?.notes || "",
            vehicleVisualModelId: damageDetection?.vehicleVisualModel?.id,
            initialAttachments: damageDetection?.attachments || undefined,
            attachments:
              damageDetection?.attachments && damageDetection.attachments.length > 0
                ? damageDetection.attachments
                : undefined,
            items: damageDetection?.items?.map((x) => mapItemToInputDto(x)) || [],
            tenantRequestsMeta:
              damageDetection?.tenantRequestsMeta || defaultValues?.tenantRequestsMeta || undefined,
            tags: damageDetection?.tags || undefined,
            isAttachmentFilesUploading: false,
            isAttachmentFilesHaveErrors: false,
            submit: "",
          }}
          validationSchema={Yup.object().shape({
            // contractId: Yup.string().required("Contract is required"),
            // vehicleId: Yup.string().required("Vehicle is required"),
            // type: Yup.string().required("Type is required"),
          })}
          onSubmit={async (values, { setFieldError, setStatus, setSubmitting }) => {
            try {
              if (formInfo.isCreate) {
                const response = await apiClient.damageDetectionsApi.apiV1DamageDetectionsPost({
                  nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                  createDamageDetectionDto: {
                    ...omit(values, ["submit"]),
                    contractId: contractIdComputed,
                    isNoDamagesDetected: !values.isAnyDamagesOnVehicle,
                    inspectedAt: values.inspectedAt
                      ? moment(values.inspectedAt).utc().format()
                      : undefined,
                    attachments: values.attachments,
                    items: values.items,
                  },
                });
                enqueueSnackbar("Damage detection created.", { variant: "success" });
                onCreate && (await onCreate(response.data));
                onSave && (await onSave(response.data));
              } else {
                const response =
                  await apiClient.damageDetectionsApi.apiV1DamageDetectionsDamageDetectionIdPut({
                    nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                    damageDetectionId: damageDetectionId || "",
                    updateDamageDetectionDto: {
                      ...omit(values, ["submit"]),
                      isNoDamagesDetected: !values.isAnyDamagesOnVehicle,
                      inspectedAt: values.inspectedAt
                        ? moment(values.inspectedAt).utc().format()
                        : undefined,
                      attachments: values.attachments,
                      items: values.items?.map((item) => ({
                        ...item,
                        id: IdHelper.isTempFeId(item?.id) ? undefined : item.id,
                      })),
                    },
                  });
                enqueueSnackbar("Damage detection updated.", { variant: "success" });
                onUpdate && (await onUpdate(response.data));
                onSave && (await onSave(response.data));
              }

              if (mounted.current) {
                setStatus({ success: true });
                setSubmitting(false);
              }
            } catch (err: any) {
              if (mounted.current) {
                ValidationHelper.handleApiErrorResponseFormik(err, setFieldError);
                setStatus({ success: false });
                setSubmitting(false);
              }
            }
          }}
        >
          {(formikProps) => {
            const {
              errors,
              handleBlur,
              handleChange,
              handleSubmit,
              isSubmitting,
              touched,
              values,
              setFieldValue,
            } = formikProps;

            return (
              <form noValidate onSubmit={handleSubmit}>
                <BaseEntityCreateUpdateExtras
                  defaultImperativeHandle={{
                    formWrapperRef: ref,
                    formikProps: formikProps,
                  }}
                  guardProps={{
                    formikProps: formikProps,
                  }}
                />

                <DevRenderCount isPositionFixed description='Form root' />

                <InferEntityAffiliationFromVehicleFormikComputedField
                  formInfo={formInfo}
                  values={{
                    vehicleId: values.vehicleId,
                    vehicle: values.vehicle,
                    departmentIds: values.departmentIds,
                    locationIds: values.locationIds,
                  }}
                  onComputed={(computed) => {
                    setFieldValue("vehicle", computed.vehicle);
                    setFieldValue("departmentIds", computed.departmentIds);
                    setFieldValue("locationIds", computed.locationIds);
                  }}
                />

                <Stack gap={1}>
                  <FormContentBlock gap={2}>
                    <MemoWrapper
                      props={{
                        isCreate: formInfo.isCreate,
                        formPerms,
                        vehicleIdComputed,
                        damageDetection,
                        contract,
                        errors,
                        tags: values.tags,
                        vehicleId: values.vehicleId,
                        vehicle: values.vehicle,
                        departmentIds: values.departmentIds || [],
                        locationIds: values.locationIds || [],
                        contractId: values.contractId,
                        inspectedAt: values.inspectedAt,
                        mileage: values.mileage,
                        fuelLevel: values.fuelLevel,
                        isUseSupplierBranding: values.isUseSupplierBranding,
                        vehicleVisualModelId: values.vehicleVisualModelId,
                        externalNumber: values.externalNumber,
                      }}
                      arePropsEqualFactory={(params) =>
                        MemoHelper.arePropsEqual.factory<typeof params.props>({
                          isExcludeFunctionProps: true,
                        })
                      }
                    >
                      {() => (
                        <Box>
                          <DevRenderCount sx={{ mb: 1 }} description='Form first section' />

                          {/* Visual model */}
                          {values.vehicleId && (
                            <VehicleVisualModelAutoSelector
                              visualModelId={values.vehicleVisualModelId}
                              filters={{
                                type: VehicleVisualModelType.TwoDimensionalDivided,
                                vehicleType: contract?.vehicle?.spec?.type,
                                bodyType: contract?.vehicle?.spec?.bodyType,
                                vehicleId: values.vehicleId || contract?.vehicle?.id,
                              }}
                              onChange={async (newValue) => {
                                if (formInfo.isCreate) {
                                  setFieldValue(`vehicleVisualModelId`, newValue?.id);
                                }
                                setVisualModel(newValue || undefined);
                              }}
                            />
                          )}

                          {/* Tags */}
                          <FormControl margin='dense' fullWidth>
                            <GeneralAttachedTagsInput
                              value={values.tags}
                              onChange={(newValue) => {
                                setFieldValue("tags", newValue);
                              }}
                            />
                            <FormHelperText error>
                              {ValidationHelper.getFormikErrorsAsString(errors.tags, {
                                isIncludeNested: false,
                              })}
                            </FormHelperText>
                          </FormControl>

                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns: { xxs: "1fr", md: "1fr 1fr" },
                              gridAutoFlow: "row dense",
                              columnGap: 1,
                            }}
                          >
                            <FormControl margin='dense' fullWidth error={Boolean(errors.vehicleId)}>
                              <VehicleAutocompleteOrCreate
                                autocompleteProps={{
                                  required: true,
                                  disabled:
                                    !formPerms.canEditVehicle || !isEmpty(vehicleIdComputed),
                                  entityId: values.vehicleId,
                                  isPreload: formPerms.canEditVehicle,
                                  // this wash used for affiliation inference probably
                                  // onSelectedLoaded: (newValue) => {
                                  //   if (
                                  //     values.vehicleId &&
                                  //     !values.vehicle &&
                                  //     values.vehicleId == newValue.id
                                  //   ) {
                                  //     setFieldValue(`vehicle`, newValue);
                                  //   }
                                  // },
                                  onChange: (newValue) => {
                                    setFieldValue(`vehicleId`, newValue?.id);
                                    setFieldValue(`vehicle`, newValue);
                                    setFieldValue("vehicleVisualModelId", undefined);
                                    setFieldValue(`contractId`, undefined);
                                    setFieldValue(`contract`, undefined);
                                    setFieldValue("items", undefined);
                                    setFieldValue(
                                      "departmentIds",
                                      values.vehicleId !== newValue?.id
                                        ? undefined
                                        : ArrayHelper.undefinedIfEmpty(values.departmentIds),
                                    );
                                    setFieldValue(
                                      "locationIds",
                                      values.vehicleId !== newValue?.id
                                        ? undefined
                                        : ArrayHelper.undefinedIfEmpty(values.locationIds),
                                    );
                                  },
                                }}
                                createFormPlacement='modal'
                                onCreate={(newValue) => {
                                  setFieldValue(`vehicleId`, newValue?.id);
                                  setFieldValue(`vehicle`, newValue);
                                  setFieldValue("vehicleVisualModelId", undefined);
                                  setVisualModel(undefined);
                                  setFieldValue(`contractId`, undefined);
                                  setFieldValue(`contract`, undefined);
                                  setFieldValue("items", undefined);
                                  setFieldValue(
                                    "departmentIds",
                                    values.vehicleId !== newValue?.id
                                      ? undefined
                                      : ArrayHelper.undefinedIfEmpty(values.departmentIds),
                                  );
                                  setFieldValue(
                                    "locationIds",
                                    values.vehicleId !== newValue?.id
                                      ? undefined
                                      : ArrayHelper.undefinedIfEmpty(values.locationIds),
                                  );
                                }}
                              />
                              <FormHelperText>{errors.vehicleId}</FormHelperText>
                            </FormControl>

                            {values.contractId && (
                              <FormControl
                                margin='dense'
                                fullWidth
                                error={Boolean(errors.contractId)}
                              >
                                <ContractAutocompleteOrCreate
                                  autocompleteProps={{
                                    disabled: !formPerms.canEditContract,
                                    entityId: values.contractId,
                                    isPreload: formPerms.canEditContract,
                                    searchFilters: {
                                      vehicleId: values.vehicleId || undefined,
                                      excludeStage: ContractStage.Draft,
                                    },
                                    onChange: (newValue) => {
                                      setFieldValue(`contractId`, newValue?.id);
                                      setFieldValue(`contract`, newValue || undefined);
                                    },
                                  }}
                                  createFormPlacement='modal'
                                  onCreate={(newValue) => {
                                    setFieldValue(`contractId`, newValue?.id);
                                    setFieldValue(`contract`, newValue || undefined);
                                  }}
                                />
                                <FormHelperText>{errors.contractId}</FormHelperText>
                              </FormControl>
                            )}

                            {/* Entity affiliation */}
                            <EntityAffiliationInputV2
                              sx={{ gridColumn: { md: "span 2" } }}
                              stackProps={{
                                direction: {
                                  md: "row",
                                  xxs: "column",
                                },
                                gap: { xxs: 0, md: 1 },
                              }}
                              departments={{
                                departmentIds: values.departmentIds,
                                onChange: (departmentIds) => {
                                  setFieldValue("departmentIds", departmentIds);
                                },
                                error: errors.departmentIds,
                              }}
                              locations={{
                                locationIds: values.locationIds,
                                onChange: (locationIds) => {
                                  setFieldValue("locationIds", locationIds);
                                },
                                error: errors.locationIds,
                              }}
                            />

                            <TextField
                              fullWidth
                              error={Boolean(touched.externalNumber && errors.externalNumber)}
                              helperText={touched.externalNumber && errors.externalNumber}
                              label='External number'
                              margin='dense'
                              name='externalNumber'
                              onChange={(newValue) => {
                                setFieldValue("externalNumber", newValue.target.value);
                              }}
                              type='text'
                              value={values?.externalNumber || ""}
                              variant='outlined'
                            />

                            {values.vehicleId && (
                              <>
                                <FormControl
                                  margin='dense'
                                  error={Boolean(touched.inspectedAt && errors.inspectedAt)}
                                >
                                  <AppDateTimePicker
                                    label='Inspected at'
                                    value={
                                      (values.inspectedAt && moment(values.inspectedAt)) || null
                                    }
                                    format={DATETIME_FORMATS.DISPLAY_DATETIME}
                                    onChange={(newValue: Moment | null) => {
                                      setFieldValue("inspectedAt", newValue?.format() || null);
                                    }}
                                  />
                                  <FormHelperText>
                                    {touched.inspectedAt && errors.inspectedAt}
                                  </FormHelperText>
                                </FormControl>

                                {/* Mileage */}
                                <MileageInput
                                  value={values.mileage}
                                  vehicle={values.vehicle}
                                  disabled={!values.vehicle || !formInfo.isCreate}
                                  fullWidth
                                  name='mileage'
                                  error={Boolean(touched.mileage && errors.mileage)}
                                  helperText={touched.mileage && errors.mileage}
                                  onBlur={handleBlur}
                                  onChange={(newValue) => {
                                    setFieldValue("mileage", newValue);
                                  }}
                                />

                                {/* Fuel level */}
                                <OutlinedInputWrapper
                                  label='Fuel level'
                                  input={
                                    <FuelLevelInput
                                      value={values.fuelLevel || 0}
                                      size='medium'
                                      onBlur={handleBlur}
                                      onChange={(e, val) => setFieldValue("fuelLevel", val || 0)}
                                    />
                                  }
                                  padding='dense'
                                  error={Boolean(touched.fuelLevel && errors.fuelLevel)}
                                  helperText={touched.fuelLevel && errors.fuelLevel}
                                  formControlProps={{ margin: "dense", fullWidth: true }}
                                />

                                {/* Include supplier branding */}
                                <FormControl margin='dense' fullWidth>
                                  <FormControlPad padding='normal'>
                                    <FormControlLabel
                                      sx={{ width: "fit-content" }}
                                      label={
                                        <Typography component='span' sx={{ display: "flex" }}>
                                          Include supplier branding
                                        </Typography>
                                      }
                                      control={
                                        <Checkbox
                                          sx={{ my: -1, p: 1 }}
                                          checked={values.isUseSupplierBranding}
                                          onChange={(e) =>
                                            setFieldValue(`isUseSupplierBranding`, e.target.checked)
                                          }
                                        />
                                      }
                                    />
                                    {errors.isUseSupplierBranding && (
                                      <FormHelperText error>
                                        {errors.isUseSupplierBranding}
                                      </FormHelperText>
                                    )}
                                  </FormControlPad>
                                </FormControl>
                              </>
                            )}
                          </Box>
                        </Box>
                      )}
                    </MemoWrapper>

                    {/* Items */}
                    {values.vehicleId && (
                      <Stack gap={1}>
                        <Typography variant='h6'>
                          Damages *{" "}
                          {/* <AppPopover
                            hoverBehavior={{ closeBehavior: "onTriggerOrContentMouseLeave" }}
                            trigger={
                              <AppIconButton size='small' color='text'>
                                <AppIcon of='info' />
                              </AppIconButton>
                            }
                          >
                            <AppPopoverContent isUseDefaultStyles={false} isTransparent>
                              <Alert severity='info' sx={{ maxWidth: { xxs: "90vw", md: 400 } }}>
                                <Box component='ul' sx={{ m: 0, pl: 2 }}>
                                  <li>Click on vehicle blueprint to add a damage.</li>
                                  <li>Click on point to view damage info or to update it.</li>
                                  <li>Move damages by dragging them.</li>
                                  <li>
                                    After submission of this operation, corresponding damages will
                                    be recorded into the vehicle history.
                                  </li>
                                </Box>
                              </Alert>
                            </AppPopoverContent>
                          </AppPopover> */}
                        </Typography>

                        <DevRenderCount description='Items section' />

                        <DamageDetectionItemsInput
                          formInfo={formInfo}
                          formPerms={formPerms}
                          damageDetection={damageDetection}
                          visualModel={visualModel}
                          formikProps={formikProps}
                        />
                      </Stack>
                    )}
                  </FormContentBlock>

                  {/* Checkboxes */}
                  {values.vehicleId && (
                    <MemoWrapper
                      props={{
                        errors,
                        contractId: values.contractId,
                        inspector: values.inspector,
                        isSendToCustomer: values.isSendToCustomer,
                        sendToExternalCostEvaluation: values.sendToExternalCostEvaluation,
                      }}
                      arePropsEqualFactory={(params) =>
                        MemoHelper.arePropsEqual.factory<typeof params.props>({
                          isExcludeFunctionProps: true,
                        })
                      }
                    >
                      {() => (
                        <FormContentBlock>
                          <Box>
                            <DevRenderCount sx={{ mb: 1 }} description='Checkboxes' />

                            {/* Send to customer */}
                            {values.contractId && (
                              <FormControl margin='none'>
                                <FormControlLabel
                                  sx={{ width: "fit-content" }}
                                  label='Send to customer'
                                  control={
                                    <Checkbox
                                      checked={values.isSendToCustomer ?? undefined}
                                      onChange={(e) =>
                                        setFieldValue("isSendToCustomer", e.target.checked)
                                      }
                                    />
                                  }
                                />
                              </FormControl>
                            )}

                            {/* Send to assessment */}
                            {formInfo.isCreate && (
                              <FormControl margin='none'>
                                <FormControlLabel
                                  sx={{ width: "fit-content" }}
                                  label={
                                    <Box>
                                      <AppTypography
                                        component='span'
                                        decoration={{ variant: "helpText" }}
                                        tooltipProps={{
                                          title:
                                            "Automatically send damages to external cost evaluation via C2C request to default assessor company, configured in your company settings.",
                                        }}
                                      >
                                        Send to assessment{" "}
                                      </AppTypography>{" "}
                                      <AppTooltip title='Configure details'>
                                        <AppIconButton
                                          variant='text'
                                          size='extraSmall'
                                          onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setIsSendToExternalDamageCostEvaluationInlineInputModalOpen(
                                              true,
                                            );
                                          }}
                                        >
                                          <AppIcon of='settings' />
                                        </AppIconButton>
                                      </AppTooltip>
                                    </Box>
                                  }
                                  control={
                                    <Checkbox
                                      checked={
                                        values.sendToExternalCostEvaluation?.isSend ?? undefined
                                      }
                                      onChange={(e) =>
                                        setFieldValue(
                                          "sendToExternalCostEvaluation.isSend",
                                          e.target.checked,
                                        )
                                      }
                                    />
                                  }
                                />
                              </FormControl>
                            )}

                            {/* Inspector */}
                            <FormControl margin='none' fullWidth>
                              <FormControlLabel
                                sx={{ width: "fit-content" }}
                                label={
                                  <AppTypography
                                    decoration={{ variant: "helpText" }}
                                    tooltipProps={{
                                      title: "Currently logged in user evaluated the damages",
                                    }}
                                  >
                                    Inspected by{" "}
                                    {values.inspector?.personName
                                      ? `${values.inspector.personName?.firstName} ${values.inspector.personName?.lastName}`
                                      : profile?.personName?.name}
                                  </AppTypography>
                                }
                                control={
                                  <Switch
                                    disabled
                                    checked={values.inspector?.isCurrentUser}
                                    onChange={handleChange}
                                    name='inspector.isCurrentUser'
                                  />
                                }
                              />
                            </FormControl>
                          </Box>
                        </FormContentBlock>
                      )}
                    </MemoWrapper>
                  )}

                  {/* Other */}
                  {values.vehicleId && (
                    <MemoWrapper
                      props={{
                        errors,
                        notes: values.notes,
                        initialAttachments: values.initialAttachments,
                        attachments: values.attachments,
                      }}
                      arePropsEqualFactory={(params) =>
                        MemoHelper.arePropsEqual.factory<typeof params.props>({
                          isExcludeFunctionProps: true,
                        })
                      }
                    >
                      {() => (
                        <FormContentBlock>
                          <FoldableBlock
                            defaultIsFolded={false}
                            spacing={0}
                            trigger={{
                              label: (
                                <Typography component='div' variant='h6'>
                                  General notes and files
                                </Typography>
                              ),
                            }}
                          >
                            <Stack>
                              <DevRenderCount sx={{ mb: 0 }} description='Form last section' />

                              <AppTypography variant='body1' gutterBottom>
                                This is the section to add general notes and files. If you want to
                                add a specific damage - use the block above.
                              </AppTypography>

                              <AppTextArea
                                error={Boolean(touched.notes && errors.notes)}
                                fullWidth
                                helperText={touched.notes && errors.notes}
                                mode='notes'
                                margin='dense'
                                name='notes'
                                onBlur={handleBlur}
                                onChange={handleChange}
                                value={values.notes}
                                variant='outlined'
                              />

                              <FormControl margin='dense' fullWidth>
                                <FileUploader
                                  multiple
                                  defaultFiles={FileItem.createManyFrom(values.initialAttachments)}
                                  onChange={(newFiles) => {
                                    setFieldValue(
                                      `attachments`,
                                      FileItem.toManyGeneralAttachmentInputDto(newFiles),
                                    );
                                  }}
                                  onUploadStarted={() => {
                                    setFieldValue("isAttachmentFilesUploading", true);
                                  }}
                                  onUploadFinished={() => {
                                    setFieldValue("isAttachmentFilesUploading", false);
                                  }}
                                  onValidationStatusChange={(filesValidationStatus) => {
                                    if (filesValidationStatus)
                                      setFieldValue(
                                        "isAttachmentFilesHaveErrors",
                                        Object.values(filesValidationStatus).some(
                                          (x) => x === false,
                                        ),
                                      );
                                  }}
                                />
                              </FormControl>
                            </Stack>
                          </FoldableBlock>
                        </FormContentBlock>
                      )}
                    </MemoWrapper>
                  )}
                </Stack>

                <GeneralValidationError sx={{ my: 1 }} errors={errors} />

                {values.vehicleId && (
                  <FormActions>
                    <Button
                      color='primary'
                      disabled={
                        values.isAttachmentFilesUploading || values.isAttachmentFilesHaveErrors
                      }
                      loading={isSubmitting}
                      type='submit'
                      variant='contained'
                    >
                      Save
                    </Button>

                    {onCancel && (
                      <Button
                        variant='outlined'
                        color='text'
                        disabled={isSubmitting}
                        onClick={onCancel}
                      >
                        Cancel
                      </Button>
                    )}
                  </FormActions>
                )}

                {/* Confirm no damages modal. */}
                <ConfirmationModal
                  title='Vehicle has no new damages?'
                  body={
                    "Please confirm that you haven't detected any new damages during the inspection."
                  }
                  open={confirmVehicleHasNoNewDamageOpen}
                  onClose={() => setConfirmVehicleHasNoNewDamageOpen(false)}
                  onCancel={() => setConfirmVehicleHasNoNewDamageOpen(false)}
                  onConfirm={() => {
                    setFieldValue("isAnyDamagesOnVehicle", false);
                    setFieldValue("items", []);
                    setConfirmVehicleHasNoNewDamageOpen(false);
                  }}
                />

                {isSendToExternalDamageCostEvaluationInlineInputModalOpen && (
                  <DamageDetectionSendToExternalDamageCostEvaluationInlineInputModal
                    open={isSendToExternalDamageCostEvaluationInlineInputModalOpen}
                    onClose={() => {
                      setIsSendToExternalDamageCostEvaluationInlineInputModalOpen(false);
                    }}
                    defaultValues={values.sendToExternalCostEvaluation}
                    onSave={(newValue) => {
                      setFieldValue("sendToExternalCostEvaluation", newValue);
                    }}
                  />
                )}
              </form>
            );
          }}
        </Formik>
      </BaseEntityCreateUpdate>
    );
  },
);
